import { type FC, useMemo } from "react";
import { Dialog, LinearProgress } from "@mui/material";
import { useApolloClient } from "@apollo/client";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useCreateTagMutation } from "@/partner/entities/tags";
import {
  mapTagToFormValues,
  mapFormValuesToCreateTagInput,
  getTagTypeCustomFields,
  getTagMutationErrorMessage,
  type TagFormValues,
} from "../model";
import { TagForm } from "./TagForm";

interface CreateTagDialogProps {
  open: boolean;
  /** Тип из фильтра списка: новый тег сразу этого типа */
  initialTypeId?: string | null;
  onClose: () => void;
  onCompleted?: () => void;
}

export const CreateTagDialog: FC<CreateTagDialogProps> = ({
  open,
  initialTypeId = null,
  onClose,
  onCompleted,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const apolloClient = useApolloClient();

  const [createTag, { loading }] = useCreateTagMutation({
    onCompleted: () => {
      enqueueSnackbar(t("tags.create.success"), {
        variant: "success",
      });
      onCompleted?.();
    },
    onError: (error) => {
      enqueueSnackbar(getTagMutationErrorMessage(error, t), {
        variant: "error",
      });
    },
    refetchQueries: ["TagsList"],
  });

  const handleSubmit = async (values: TagFormValues) => {
    // См. EditTagDialog: сетевой сбой чтения определений типа не должен
    // оставлять форму молча закрытой без сообщения об ошибке.
    let customFields;
    try {
      customFields = await getTagTypeCustomFields(apolloClient, values.typeId);
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : String(error), {
        variant: "error",
      });
      return;
    }

    // Диалог закрывается только через onCompleted мутации (см. выше):
    // при отказе (например TAG_CUSTOM_FIELD_INVALID) форма остаётся
    // открытой с введёнными значениями, а не закрывается вслепую.
    await createTag({
      variables: {
        input: mapFormValuesToCreateTagInput(values, customFields),
      },
    });
  };

  const initialValues = useMemo(
    () => ({ ...mapTagToFormValues(null), typeId: initialTypeId }),
    [initialTypeId]
  );

  return (
    <Dialog fullScreen scroll="body" open={open} onClose={onClose}>
      {loading && <LinearProgress />}
      <TagForm
        initialValues={initialValues}
        onSubmit={handleSubmit}
        onClose={onClose}
        title={t("tags.create.title")}
        loading={loading}
      />
    </Dialog>
  );
};
